import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Calendar, Users, Clock, Radio } from "lucide-react";

/**
 * FeaturedRoomsStrip — floating pill on the top-right of the reels feed showing the
 * TOP 3 Featured Rooms (upcoming scheduled first, then live rooms as fallback).
 * Rotates every 4s. Tap → /rooms?open={id}. Auto-hides when there's zero audio activity.
 */
function fmtCountdown(iso) {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "Now";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function FeaturedRoomsStrip() {
  const [rooms, setRooms] = useState([]);
  const [idx, setIdx] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get("/rooms/featured").then(r => {
        if (!cancelled) setRooms(Array.isArray(r.data) ? r.data : []);
      }).catch(() => { if (!cancelled) setRooms([]); });
    };
    load();
    const t = setInterval(load, 30000); // refresh every 30s
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (rooms.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % rooms.length), 4000);
    return () => clearInterval(t);
  }, [rooms.length]);

  if (rooms.length === 0) return null;
  const r = rooms[idx] || rooms[0];
  const isLive = r.kind === "live";

  // Live rooms → red theme (matches LiveNowStrip pattern), scheduled → gold theme
  const themeBorder = isLive ? "border-[#FF2A5F]/60" : "border-[#FF7A00]/55";
  const themeShadow = isLive ? "shadow-[0_0_24px_rgba(255,42,95,0.30)]" : "shadow-[0_0_24px_rgba(255,215,0,0.30)]";
  const avatarBorder = isLive ? "border-[#FF2A5F]" : "border-[#FF7A00]";
  const badgeBg = isLive ? "bg-[#FF2A5F]" : "bg-[#FF7A00]";
  const badgeIcon = isLive ? <Radio className="w-2 h-2 text-white" strokeWidth={3}/> : <Calendar className="w-2 h-2 text-black" strokeWidth={3}/>;
  const labelColor = isLive ? "text-[#FF2A5F]" : "text-[#FF7A00]";
  const labelText = isLive ? "Live Room" : "Featured";
  const metaColor = isLive ? "text-[#FF2A5F]" : "text-[#FF7A00]";

  return (
    <button
      onClick={() => nav(`/rooms?open=${r.id}`)}
      data-testid="featured-rooms-strip"
      data-kind={r.kind}
      className={`fixed top-16 right-3 z-30 flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-black/70 backdrop-blur-md border ${themeBorder} ${themeShadow} active:scale-95 animate-in fade-in duration-500 max-w-[220px]`}
    >
      <div className="relative flex-shrink-0">
        <img
          src={r.host_avatar || "https://api.dicebear.com/7.x/avataaars/svg?seed=roth"}
          alt=""
          className={`w-8 h-8 rounded-full object-cover border-2 ${avatarBorder}`}
        />
        <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ${badgeBg} border-2 border-black flex items-center justify-center ${isLive ? "animate-pulse" : ""}`}>
          {badgeIcon}
        </span>
      </div>
      <div className="min-w-0 text-left">
        <div className={`flex items-center gap-1 text-[9px] font-black uppercase tracking-wider ${labelColor}`}>
          {isLive ? <Radio className="w-3 h-3"/> : <Calendar className="w-3 h-3"/>} {labelText}
        </div>
        <div data-testid="featured-rooms-title" className="text-[11px] font-bold text-white truncate max-w-[140px]">{r.title}</div>
      </div>
      <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
        {isLive ? (
          <div className={`flex items-center gap-0.5 text-[9px] font-black ${metaColor}`}>
            <Users className="w-2.5 h-2.5"/><span data-testid="featured-rooms-eta">{r.listener_count || 0}</span>
          </div>
        ) : (
          <>
            <div className={`flex items-center gap-0.5 text-[9px] font-black ${metaColor}`}>
              <Clock className="w-2.5 h-2.5"/><span data-testid="featured-rooms-eta">{fmtCountdown(r.scheduled_at)}</span>
            </div>
            <div className="flex items-center gap-0.5 text-[9px] font-bold text-white/80">
              <Users className="w-2.5 h-2.5"/>{r.rsvp_count || 0}
            </div>
          </>
        )}
      </div>
    </button>
  );
}
